import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowRight, Menu, X, Send, ExternalLink, Sparkles } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import CookieConsent from './components/CookieConsent';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Site URL for QR
const SITE_URL = 'https://seagullbyte.it';

// vCard data for contact QR
const VCARD_DATA = `BEGIN:VCARD
VERSION:3.0
FN:SeagullByte
ORG:SeagullByte
EMAIL:info@seagullbyte.it
URL:https://seagullbyte.it
NOTE:Software, AI & Consulting
END:VCARD`;

// Logo Component
const Logo = ({ size = 'normal' }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_ai-consulting-pro-2/artifacts/dxcsd9rq_seagullbyte-removebg-preview%20%281%29.png" 
    alt="SeagullByte Logo" 
    className={size === 'large' ? 'h-28 md:h-36 w-auto' : 'h-20 md:h-24 w-auto'}
    data-testid="logo"
  />
);

// Aurora Border Component - Clean white background
const AuroraBorder = ({ children, className = '' }) => (
  <div className={`aurora-border bg-white ${className}`}>
    {children}
  </div>
);

// AI Tech Icon Component
const AIIcon = ({ type, className = '' }) => {
  const icons = {
    software: (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
        <path d="M4 7V4h16v3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 20h6" strokeLinecap="round"/>
        <path d="M12 17v3" strokeLinecap="round"/>
        <rect x="2" y="7" width="20" height="10" rx="2"/>
        <path d="M6 12h.01M10 12h.01M14 12h.01M18 12h.01" strokeLinecap="round"/>
        <path d="M8 14l2-4 2 4M14 10v4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    ai: (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
        <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        <circle cx="12" cy="12" r="9" strokeDasharray="4 2"/>
      </svg>
    ),
    consulting: (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5"/>
        <path d="M2 12l10 5 10-5"/>
        <circle cx="12" cy="12" r="2" fill="currentColor"/>
      </svg>
    ),
    contact: (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
        <circle cx="12" cy="12" r="2" strokeDasharray="2 1"/>
      </svg>
    ),
  };
  return icons[type] || null;
};

// AI Button Component
const AIButton = ({ children, onClick, disabled, type = 'button', className = '' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`ai-button group relative overflow-hidden ${className}`}
    data-testid="lead-form-submit-button"
  >
    <span className="ai-button-bg" />
    <span className="ai-button-glow" />
    <span className="relative z-10 flex items-center justify-center gap-2 font-semibold">
      {children}
    </span>
  </button>
);

// Navbar
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { name: 'Servizi', href: '#servizi' },
    { name: 'Prodotti', href: '#prodotti' },
    { name: 'Progetti', href: '#progetti' },
    { name: 'Contatti', href: '#contatti' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-28">
          <Link to="/" data-testid="nav-link-home">
            <Logo size="normal" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors text-sm"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <button 
              className="btn-primary text-sm"
              data-testid="btn-cta-nav"
              onClick={() => document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Parla con noi
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-600"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="btn-mobile-menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden pb-6 border-t border-gray-100"
          >
            <div className="flex flex-col gap-4 pt-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <button 
                className="btn-primary text-sm mt-2"
                onClick={() => {
                  setIsOpen(false);
                  document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Parla con noi
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center relative overflow-hidden bg-[#FAFBFC]">
      {/* Subtle aurora glows */}
      <div className="aurora-glow bg-blue-400/20 top-20 -left-40" />
      <div className="aurora-glow bg-purple-400/20 top-40 right-0" style={{ animationDelay: '2s' }} />
      <div className="aurora-glow bg-emerald-400/15 bottom-20 left-1/3" style={{ animationDelay: '4s' }} />

      <div className="relative max-w-6xl mx-auto px-6 py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-blue-600 font-semibold text-sm tracking-wide uppercase mb-6"
          >
            Software · AI · Consulting
          </motion.p>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-tight mb-6">
            Trasformiamo le idee in{' '}
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
              soluzioni intelligenti
            </span>
          </h1>
          
          <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            Sviluppo software, piattaforme AI e consulenza IT per automatizzare, innovare e scalare il tuo business con tecnologie all'avanguardia.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="btn-explore-services"
              onClick={() => document.getElementById('servizi')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Scopri i Servizi
              <ArrowRight className="h-4 w-4" />
            </button>
            <button 
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="btn-request-consult"
              onClick={() => document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Consulenza
            </button>
          </div>
        </motion.div>

        {/* Stats card with aurora border */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-20"
        >
          <AuroraBorder className="p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">10+</p>
                <p className="text-gray-500 text-sm mt-1">Anni di esperienza</p>
              </div>
              <div>
                <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">50+</p>
                <p className="text-gray-500 text-sm mt-1">Progetti realizzati</p>
              </div>
              <div>
                <p className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">100%</p>
                <p className="text-gray-500 text-sm mt-1">Clienti soddisfatti</p>
              </div>
            </div>
          </AuroraBorder>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: 'software',
      title: 'Sviluppo Software',
      description: 'Soluzioni software scalabili e customizzate per ottimizzare i flussi aziendali.',
    },
    {
      icon: 'ai',
      title: 'Piattaforme AI',
      description: 'Integrazione di modelli LLM per automatizzare processi e generare contenuti.',
    },
    {
      icon: 'consulting',
      title: 'Consulenza IT',
      description: 'Strategie IT avanzate e trasformazione digitale guidata da esperti.',
    },
  ];

  return (
    <section id="servizi" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-blue-600 font-semibold text-sm tracking-wide uppercase mb-3">
            Cosa facciamo
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            I Nostri Servizi
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <AuroraBorder className="p-8 h-full card-hover">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center mb-6 relative">
                  <AIIcon type={service.icon} className="w-8 h-8 text-blue-600" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-500 leading-relaxed">
                  {service.description}
                </p>
              </AuroraBorder>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Products Section
const ProductsSection = () => {
  const products = [
    {
      name: 'CVForge',
      tagline: 'Ottimizza il tuo CV per i sistemi ATS',
      description: 'Piattaforma intelligente che analizza e ottimizza il tuo curriculum per superare i filtri automatici delle piattaforme di recruiting. Acquisto singolo o pacchetti.',
      image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop&q=60',
      link: 'https://cvforge.seagullbyte.it',
      features: ['Analisi ATS', 'Ottimizzazione keywords', 'Suggerimenti AI', 'Export multipli'],
    },
  ];

  return (
    <section id="prodotti" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-blue-600 font-semibold text-sm tracking-wide uppercase mb-3">
            I Nostri Prodotti
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Soluzioni Pronte all'Uso
          </h2>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {products.map((product, index) => (
            <motion.div
              key={product.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <AuroraBorder className="overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative h-64 md:h-auto overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent md:bg-gradient-to-t" />
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold mb-4 w-fit">
                      <Sparkles className="h-3 w-3" />
                      PRODOTTO
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-blue-600 font-medium mb-4">
                      {product.tagline}
                    </p>
                    <p className="text-gray-500 leading-relaxed mb-6">
                      {product.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {product.features.map((feature, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <a 
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary inline-flex items-center justify-center gap-2 w-fit"
                      data-testid="product-link-cvforge"
                    >
                      Scopri CVForge
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </AuroraBorder>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const projects = [
    {
      name: 'StartupAI',
      description: 'AI per generazione automatica di news e rassegne stampa tramite LLM.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&auto=format&fit=crop&q=60',
      link: null,
    },
    {
      name: 'StartupItalia.eu',
      description: "L'hub italiano dell'innovazione e magazine per l'ecosistema startup.",
      image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&auto=format&fit=crop&q=60',
      link: 'https://startupitalia.eu',
    },
    {
      name: 'VirtualVillage.eu',
      description: 'Piattaforma immersiva per networking tra startup e investitori.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=60',
      link: 'https://virtualvillage.eu',
    },
    {
      name: 'SIOS',
      description: "Supporto tech per l'evento leader italiano sull'innovazione.",
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&auto=format&fit=crop&q=60',
      link: 'https://startupitaliaopensummit.eu',
    },
  ];

  return (
    <section id="progetti" className="py-24 md:py-32 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-blue-600 font-semibold text-sm tracking-wide uppercase mb-3">
            Portfolio
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Progetti e Piattaforme
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <AuroraBorder className="overflow-hidden card-hover">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-6 right-6">
                    <h3 className="text-xl font-semibold text-white">{project.name}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-500 mb-4">{project.description}</p>
                  {project.link && (
                    <a 
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                      data-testid={`project-link-${index}`}
                    >
                      Visita il sito <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </AuroraBorder>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyAccepted) {
      toast.error('Devi accettare la Privacy Policy per procedere.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success('Richiesta inviata con successo! Ti contatteremo presto.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      setPrivacyAccepted(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contatti" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-blue-600 font-semibold text-sm tracking-wide uppercase mb-3">
              Contatti
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Iniziamo a costruire insieme
            </h2>
            <p className="text-gray-500 leading-relaxed mb-8">
              Pronto per trasformare la tua azienda? Lascia i tuoi dati e scopri come l'intelligenza artificiale può portare il tuo business al livello successivo.
            </p>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center relative">
                <AIIcon type="contact" className="w-6 h-6 text-blue-600" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Email</p>
                <a href="mailto:info@seagullbyte.it" className="text-gray-900 font-medium hover:text-blue-600 transition-colors">
                  info@seagullbyte.it
                </a>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <AuroraBorder className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Il tuo nome"
                      className="input-clean w-full"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="la@tuaemail.com"
                      className="input-clean w-full"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+39 123 456 7890"
                      className="input-clean w-full"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Azienda
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nome azienda"
                      className="input-clean w-full"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Messaggio
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Raccontaci il tuo progetto..."
                    rows={4}
                    className="input-clean w-full resize-none"
                    data-testid="input-message"
                  />
                </div>

                {/* Privacy Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="checkbox-privacy"
                    required
                  />
                  <label htmlFor="privacy" className="text-sm text-gray-600 leading-relaxed">
                    Ho letto e accetto la{' '}
                    <Link to="/privacy-policy" className="text-blue-600 hover:underline" target="_blank">
                      Privacy Policy
                    </Link>{' '}
                    e acconsento al trattamento dei miei dati personali per rispondere alla mia richiesta. *
                  </label>
                </div>

                <AIButton type="submit" disabled={isSubmitting || !privacyAccepted} className="w-full">
                  {isSubmitting ? (
                    <>
                      <div className="spinner w-5 h-5" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Invia Richiesta
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </AIButton>
              </form>
            </AuroraBorder>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-gray-50 border-t border-gray-100 py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-row justify-between items-center gap-6 mb-8">
          {/* Logo and info */}
          <div className="flex-shrink-0">
            <Logo size="large" />
            <p className="text-gray-500 text-xs mt-2 leading-relaxed hidden sm:block max-w-[200px]">
              Software, AI e Consulenza IT
            </p>
            <a 
              href="mailto:info@seagullbyte.it"
              className="text-blue-600 hover:text-blue-700 font-medium text-xs mt-2 hidden sm:inline-block transition-colors"
              data-testid="footer-email"
            >
              info@seagullbyte.it
            </a>
          </div>

          {/* QR Code - Website */}
          <div className="text-center flex-shrink-0">
            <div className="inline-block p-2 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100">
              <QRCodeSVG 
                value={SITE_URL}
                size={80}
                level="M"
                includeMargin={false}
                fgColor="#1A1A2E"
              />
            </div>
            <p className="text-gray-500 text-[10px] sm:text-xs mt-2 font-medium">Visita il sito</p>
          </div>

          {/* QR Code - vCard */}
          <div className="text-center flex-shrink-0">
            <div className="inline-block p-2 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100">
              <QRCodeSVG 
                value={VCARD_DATA}
                size={80}
                level="M"
                includeMargin={false}
                fgColor="#1A1A2E"
              />
            </div>
            <p className="text-gray-500 text-[10px] sm:text-xs mt-2 font-medium">Salva in rubrica</p>
          </div>
        </div>

        {/* Links */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-6">
          <Link to="/privacy-policy" className="text-gray-500 hover:text-blue-600 text-xs transition-colors">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" className="text-gray-500 hover:text-blue-600 text-xs transition-colors">
            Cookie Policy
          </Link>
        </div>

        {/* Company info and Copyright */}
        <div className="border-t border-gray-200 pt-6">
          <p className="text-gray-400 text-xs text-center">
            SeagullByte · P.IVA 01295100950
          </p>
          <p className="text-gray-400 text-xs text-center mt-1">
            © 2026 SeagullByte. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => (
  <>
    <HeroSection />
    <ServicesSection />
    <ProductsSection />
    <ProjectsSection />
    <ContactSection />
  </>
);

// Main App
function App() {
  return (
    <Router>
      <div className="App bg-[#FAFBFC] min-h-screen">
        <Toaster position="top-right" richColors />
        <Navbar />
        <main className="pt-28">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
          </Routes>
        </main>
        <Footer />
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
